import { Suspense, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, LoaderIcon } from 'lucide-react';
import { Form } from '@/components/ui/form.tsx';
import {
  Stepper,
  StepperItem,
  StepperTrigger,
  StepperIndicator,
} from '@/components/ui/stepper';
import { cn } from '@/utils/utils.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useScrollToTopHTMLElement } from '@/hooks/use-scroll-to-top-html-element.ts';
import { usePostEventRuleMutation } from '@/features/event-rules/hooks/mutations/use-post-event-rule.ts';
import { CREATE_EVENT_RULES_STEPS } from './create-event-rule-steps.tsx';
import {
  createEventRuleSchema,
  EventRuleFormData,
} from '@/features/event-rules/schemas/create-event-rules.schema.ts';

const INITIAL_STEP = 1;
const DEFAULT_COOLDOWN_PERIOD = 60;

export const CreateEventRuleForm = ({ onClose }: { onClose: () => void }) => {
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
  const isLastStep = currentStep === CREATE_EVENT_RULES_STEPS.length;
  const isFirstStep = currentStep === INITIAL_STEP;

  const { ref, scrollToTop } = useScrollToTopHTMLElement<HTMLDivElement>();

  const form = useForm({
    resolver: zodResolver(createEventRuleSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      status: 'enabled',
      eventTitle: undefined,
      ruleType: 'plant',
      plantId: undefined,
      deviceType: undefined,
      deviceId: undefined,
      conditions: [{ fact: '', operator: 'equal', value: '' }],
      eventTemplate: undefined,
      eventPreviewTemplate: undefined,
      cooldownPeriod: DEFAULT_COOLDOWN_PERIOD,
      forwardTo: [],
    },
  });

  const resetForm = () => {
    setCurrentStep(INITIAL_STEP);
    form.reset({
      status: 'enabled',
      eventTitle: undefined,
      ruleType: 'plant',
      plantId: undefined,
      deviceType: undefined,
      deviceId: undefined,
      conditions: [{ fact: '', operator: 'equal', value: '' }],
      eventTemplate: undefined,
      eventPreviewTemplate: undefined,
      cooldownPeriod: DEFAULT_COOLDOWN_PERIOD,
      forwardTo: [],
    });
  };

  const { mutate: createEventRuleMutation, isPending: isCreateEventRuleMutationPending } =
    usePostEventRuleMutation({
      onSuccess: () => {
        onClose();
        resetForm();
      },
    });

  const onSubmit = (data: EventRuleFormData) => {
    createEventRuleMutation({
      ...data,
    });
  };

  const nextStep = async () => {
    const currentStepSchema = CREATE_EVENT_RULES_STEPS[currentStep - 1].schema;
    const fieldsToValidate = Object.keys(
      currentStepSchema.shape
    ) as (keyof EventRuleFormData)[];

    const isValid = await form.trigger(fieldsToValidate);

    if (isValid && !isLastStep) {
      scrollToTop();
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // fields to reset when changing the ruleType
  // eslint-disable-next-line react-hooks/incompatible-library
  const watchRuleType = form.watch('ruleType');
  useEffect(() => {
    form.setValue('deviceType', undefined);
    form.setValue('deviceId', undefined);
    form.setValue('conditions', [{ fact: '', operator: 'equal', value: '' }]);
    form.setValue('eventTemplate', '');
    form.setValue('eventPreviewTemplate', '');
    form.setValue('forwardTo', []);
  }, [watchRuleType]);

  // fields to reset when changing the plantId
  const watchPlantId = form.watch('plantId');
  useEffect(() => {
    form.setValue('forwardTo', []);
  }, [watchPlantId]);

  const CurrentStepElement = CREATE_EVENT_RULES_STEPS[currentStep - 1].Element;

  return (
    <div ref={ref} className="max-h-[60vh] overflow-auto px-0.5">
      <CreateEventRuleFormStepper steps={CREATE_EVENT_RULES_STEPS} currentStep={currentStep} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Suspense
            fallback={
              <div className="flex h-20 items-center justify-center">
                <LoadingSpinner size={50} />
              </div>
            }
          >
            <CurrentStepElement />
          </Suspense>

          <NavigationButtons
            currentStep={currentStep}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            isCreateEventRuleMutationPending={isCreateEventRuleMutationPending}
            onPrevious={prevStep}
            onNext={nextStep}
          />
        </form>
      </Form>
    </div>
  );
};

function NavigationButtons({
  isFirstStep,
  isLastStep,
  onPrevious,
  isCreateEventRuleMutationPending,
  onNext,
}: {
  currentStep: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isCreateEventRuleMutationPending: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex justify-end">
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
          className={cn('flex items-center gap-2', isFirstStep && 'hidden')}
          disabled={isCreateEventRuleMutationPending}
        >
          <ArrowLeft size={16} />
          Previous
        </Button>

        <Button
          type="submit"
          className={cn(!isLastStep && 'hidden')}
          disabled={isCreateEventRuleMutationPending}
        >
          {isCreateEventRuleMutationPending && (
            <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
          )}
          Create Rule
        </Button>
        <Button
          type="button"
          onClick={onNext}
          className={cn('flex items-center gap-2', isLastStep && 'hidden')}
          disabled={isCreateEventRuleMutationPending}
        >
          Next
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function CreateEventRuleFormStepper({
  steps,
  currentStep,
}: {
  steps: readonly { step: number }[];
  currentStep: number;
}) {
  return (
    <Stepper value={currentStep} className="items-start gap-4">
      {steps?.map(({ step }) => (
        <StepperItem key={step} step={step} className="flex-1">
          <StepperTrigger asChild className="w-full flex-col items-start gap-2 rounded">
            <div>
              <StepperIndicator asChild className="bg-border h-1 w-full">
                <span className="sr-only">{step}</span>
              </StepperIndicator>
            </div>
          </StepperTrigger>
        </StepperItem>
      ))}
    </Stepper>
  );
}

export default CreateEventRuleForm;
